import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import {
  ContentEditableInputComponent
} from '../../../../shared/components/content-editable-input/content-editable-input.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import { KeyboardComponent } from '../../../../shared/components/keyboard/keyboard.component';
import { DescriptionModalComponent } from '../../../../shared/components/description-modal/description-modal.component';
import { SafesService } from '../../core/services/SafesService';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { SafeActions } from '../../core/enums/SafeActions';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { SelectComponent } from '../../../../shared/components/select/select.component';
import { FormsModule } from '@angular/forms';
import { IIdAndName } from '../../../../core/interfaces/IIdAndName';
import { DatevStore } from '../../../datev/core/stores/DatevStore';
import { finalize } from 'rxjs';
import { WorkerService } from '../../../worker/core/services/WorkerService';
import { WebCashPermissionControlDirective } from '../../../auth/core/directives/web-cash-permission-control.directive';

@Component({
  selector: 'app-cash-in-out-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    ContentEditableInputComponent,
    ContentBoxComponent,
    KeyboardComponent,
    DescriptionModalComponent,
    SelectComponent,
    FormsModule,
    WebCashPermissionControlDirective,
  ],
  templateUrl: './cash-in-out-modal.component.html',
  styleUrl: './cash-in-out-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CashInOutModalComponent extends BaseModalComponent<{ type: 'in' | 'out' }> {

  @ViewChild('datevAccountPickerModal') datevAccountPickerModal: ModalComponent;
  @ViewChild(ContentEditableInputComponent) contentEditableInputComponent: ContentEditableInputComponent;

  public amountValue: string;
  public remark: string;
  public datevId: number;
  public readonly datevOptions: IIdAndName<number>[] = [
    {
      id: -1,
      name: '- Keine Angabe -'.trans('safes/cash-in-out-modal/datev-account-picker-modal/options.NoData')
    },
    ...inject(DatevStore).datevAccounts()
  ];

  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _safesService = inject(SafesService);
  private readonly _workerService = inject(WorkerService);

  public override open(val: { type: 'in' | 'out' }): void {
    if (!this.permissionService.hasPermission(this.permissionKeys.WebCxCashSafes)) {
      return;
    }

    super.open(val);
    this.remark = null;
    this.amountValue = null;
    this.datevId = this.datevOptions[0].id;
    this.cdr.detectChanges();

  }

  public get isConfirmDisabled(): boolean {
    return !this.amountValue || Number(this.amountValue) <= 0;
  }

  public confirmProcess(btn: HTMLButtonElement): void {
    if (!this.permissionService.hasPermission(this.permissionKeys.WebCxCashSafes)) {
      return;
    }

    btn.classList.add('disabled');

    this._safesService.makeSafeAction(
      this._terminalsStore.selectedTerminal().currentSession.safeId,
      this.contentEditableInputComponent.value.fromCurrency(),
      false,
      this.data.type === 'in' ? SafeActions.CashIn : SafeActions.CashOut,
      this.remark,
      this.datevId === -1 ? null : this.datevId
    )
      .pipe(
        finalize(() => btn.classList.remove('disabled')),
      )
      .subscribe({
        next: () => {
          if (this.terminalParametersMap()[this.terminalParameterKeys.CSH_SAFE_PORT] && !this.isTerminalWithoutSafe) {
            this._workerService.openSafe().subscribe({
              error: ({ error }: ApiErrorResponse) => {
                this.notificationService.pushError(
                  'Fehler'.trans('notifications/error.Title'),
                  error.message.trans(`open-safe/notifications/${error.message}.Message`)
                );
              }
            });
          }

          this.datevAccountPickerModal.close();
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`make-safe-action/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public handleKeyboardClear(): void {
    this.updateAmountValue('');
  }

  public handleKeyboardDeleteBack(): void {
    this.updateAmountValue(this.amountValue.substring(0, this.amountValue.length - 1));
  }

  public handleKeyboardKeyClick(val: string): void {
    this.updateAmountValue(this.amountValue ? `${this.amountValue}${val}` : val);
  }

  private updateAmountValue(val: string): void {
    this.amountValue = val;

    if (this.contentEditableInputComponent) {
      this.contentEditableInputComponent.value = this.amountValue;
    }

    this.cdr.detectChanges();
  }
}
